import { DataGrid } from '@mui/x-data-grid';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTableFooter } from '../table-footer/table-footer.component';
import { useVisibleColumns } from '../../hooks/visible-columns.hook';
import { useTablePagination } from '../../hooks/table-pagination.hook';
import { Loading } from '../loading/loading.component';

interface Props extends Omit<ComponentProps<typeof DataGrid>, 'pagination'> {
    page?: number;
    setPage?: (page: number) => void;
    pageSize?: number;
    setPageSize?: (pageSize: number) => void;
    defaultColumns?: string[];
    prefix?: string;
    pagination?: boolean;
}
export function Table({
    page,
    setPage,
    pageSize = 5,
    setPageSize,
    pagination = true,
    paginationMode = 'server',
    sortingMode = 'server',
    disableColumnMenu = true,
    defaultColumns = [],
    prefix = window.location.pathname,
    ...props
}: Props) {
    const { t } = useTranslation();

    const { visibleColumns, onColumnVisibilityModelChange } = useVisibleColumns({
        columnNames: props.columns?.map(({ field }) => field) ?? [],
        disableColumnMenu,
        defaultColumns,
        storagePrefix: prefix,
    });

    const { paginationModel, onPaginationModelChange, paginationOptions } = useTablePagination({
        pagination,
        paginationMode,
        page,
        setPage,
        pageSize,
        setPageSize,
    });

    if (props.loading) return <Loading />;

    return (
        <DataGrid
            sortingMode={sortingMode}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingOrder={['asc', 'desc']}
            columnVisibilityModel={visibleColumns}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            initialState={visibleColumns}
            disableColumnFilter
            disableColumnMenu={disableColumnMenu}
            slots={{ footer: CustomTableFooter }}
            pageSizeOptions={props.pageSizeOptions ? props.pageSizeOptions : [5, 10, 20]}
            localeText={{
                noRowsLabel: t('noRows'),
                columnMenuHideColumn: t('columnMenuHideColumn'),
                columnMenuManageColumns: t('columnMenuManageColumns'),
                columnMenuSortAsc: t('columnMenuSortAsc'),
                columnMenuSortDesc: t('columnMenuSortDesc'),
                columnsManagementSearchTitle: t('search'),
                columnsManagementShowHideAllText: t('columnsManagementShowHideAllText'),
            }}
            sx={{
                '.MuiStack-root': { height: '100%' },
                button: { p: 1 },
                ...props.sx,
            }}
            {...paginationOptions}
            {...props}
        />
    );
}
